import { useEffect, type ReactNode } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import styled, { ThemeProvider } from 'styled-components';
import { BaseModalBackground, ModalProvider } from 'styled-react-modal';
import { GlobalStyle } from '@/styles/globalStyles';
import { Auth0ProviderWithConfig } from '@/auth/Auth0ProviderWithConfig';
import {
    TenantConfigProvider,
    useTenantConfig,
} from '@/tenant/TenantConfigProvider';
import { appThemeFor } from '@/tenant/appTheme';
import { applyTenantDocumentIdentity } from '@/tenant/tenantBranding';
import { queryClient } from '@/api/queryClient';
import { router } from '@/router';

const ModalBackdrop = styled(BaseModalBackground as any)`
    z-index: 3000;
`;

// Theme (and the theme-dependent GlobalStyle) derive from the resolved
// tenant, so this lives inside TenantConfigProvider — which blocks render
// until the config settles, so there is no unthemed flash.
function TenantThemeProvider({ children }: { children: ReactNode }) {
    const tenantConfig = useTenantConfig();

    useEffect(() => {
        applyTenantDocumentIdentity(tenantConfig);
    }, [tenantConfig]);

    return (
        <ThemeProvider theme={appThemeFor(tenantConfig)}>
            <GlobalStyle />
            {children}
        </ThemeProvider>
    );
}

/**
 * Root of the SPA. Provider order mirrors the old Remix `root.tsx` tree, with
 * QueryClient + Auth0 added (they replace loaders/actions and the server-side
 * auth session respectively).
 */
export function App() {
    return (
        <TenantConfigProvider>
            <TenantThemeProvider>
                <ModalProvider backgroundComponent={ModalBackdrop}>
                    <QueryClientProvider client={queryClient}>
                        {/* Auth0 mounts after tenant resolution: login (and
                            silent renewal) is scoped to the tenant's Auth0
                            Organization. */}
                        <Auth0ProviderWithConfig>
                            <RouterProvider router={router} />
                        </Auth0ProviderWithConfig>
                    </QueryClientProvider>
                </ModalProvider>
            </TenantThemeProvider>
        </TenantConfigProvider>
    );
}
